/**
 * AdminBankAccounts - Bank Account Management for Check Printing
 * 
 * Features:
 * - CRUD for bank accounts
 * - Default account selection
 * - Check number tracking
 * - Masked account numbers for security
 */
import React, { useState, useEffect } from 'react';
import {
  Plus, Pencil, Trash2, Loader2, Building, Star,
  Eye, EyeOff, CheckCircle, XCircle, RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { bankAccountsAPI } from '../../utils/api';
import { toast } from 'sonner';

const AdminBankAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    account_name: '',
    bank_name: '',
    routing_number: '',
    account_number: '',
    starting_check_number: 1001,
    is_default: false
  });
  
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const data = await bankAccountsAPI.getAccounts({});
      setAccounts(data || []);
    } catch (err) {
      toast.error('Failed to load bank accounts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchAccounts();
  }, []);
  
  const resetForm = () => {
    setFormData({
      account_name: '',
      bank_name: '',
      routing_number: '',
      account_number: '',
      starting_check_number: 1001,
      is_default: false
    });
    setShowAccountNumber(false);
  };
  
  const openCreateModal = () => {
    resetForm();
    setEditingAccount(null);
    setShowModal(true);
  };
  
  const openEditModal = (account) => {
    setEditingAccount(account);
    setFormData({
      account_name: account.account_name || '',
      bank_name: account.bank_name || '',
      routing_number: account.routing_number || '',
      account_number: '', // Don't populate for security
      starting_check_number: account.next_check_number || 1001,
      is_default: account.is_default || false
    });
    setShowAccountNumber(false);
    setShowModal(true);
  };
  
  const handleSave = async () => {
    // Validation
    if (!formData.account_name.trim()) {
      toast.error('Account name is required');
      return;
    }
    if (!formData.bank_name.trim()) {
      toast.error('Bank name is required');
      return;
    }
    if (!editingAccount) {
      // New account requires all fields
      if (!/^\d{9}$/.test(formData.routing_number)) {
        toast.error('Routing number must be exactly 9 digits');
        return;
      }
      if (!formData.account_number || formData.account_number.length < 4) {
        toast.error('Account number must be at least 4 digits');
        return;
      }
      if (formData.starting_check_number < 1) {
        toast.error('Starting check number must be at least 1');
        return;
      }
    }
    
    setSaving(true);
    try {
      if (editingAccount) {
        // Update - only send changed fields
        const updates = {
          account_name: formData.account_name,
          bank_name: formData.bank_name,
          is_default: formData.is_default
        };
        if (formData.routing_number && formData.routing_number !== editingAccount.routing_number) {
          updates.routing_number = formData.routing_number;
        }
        if (formData.account_number) {
          updates.account_number = formData.account_number;
        }
        await bankAccountsAPI.updateAccount(editingAccount.id, updates);
        toast.success('Bank account updated');
      } else {
        // Create
        await bankAccountsAPI.createAccount(formData);
        toast.success('Bank account created');
      }
      setShowModal(false);
      resetForm();
      fetchAccounts();
    } catch (err) {
      toast.error(err.message || 'Failed to save bank account');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSetDefault = async (account) => {
    try {
      await bankAccountsAPI.setDefault(account.id);
      toast.success(`${account.account_name} set as default`);
      fetchAccounts();
    } catch (err) {
      toast.error(err.message || 'Failed to set default');
    }
  };
  
  const handleDelete = async (account) => {
    if (!window.confirm(`Delete "${account.account_name}"? This cannot be undone if there are no checks associated.`)) {
      return;
    }
    
    try {
      await bankAccountsAPI.deleteAccount(account.id);
      toast.success('Bank account deleted');
      fetchAccounts();
    } catch (err) {
      toast.error(err.message || 'Failed to delete bank account');
    }
  };
  
  return (
    <div className="space-y-6" data-testid="admin-bank-accounts">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Bank Accounts</h2>
          <p className="text-gray-500 text-sm mt-1">
            Manage bank accounts for check printing
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchAccounts}
            disabled={loading}
            data-testid="refresh-accounts-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={openCreateModal}
            data-testid="add-bank-account-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Bank Account
          </Button>
        </div>
      </div>
      
      {/* Accounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
          </div>
        ) : accounts.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg border p-8 text-center">
            <Building className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-4">No bank accounts configured</p>
            <Button onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Account
            </Button>
          </div>
        ) : (
          accounts.map((account) => (
            <div
              key={account.id}
              className={`bg-white rounded-lg border p-5 relative ${
                account.is_default ? 'border-gold-400 ring-2 ring-gold-100' : ''
              } ${!account.is_active ? 'opacity-60' : ''}`}
              data-testid={`bank-account-card-${account.id}`}
            >
              {/* Default Badge */}
              {account.is_default && (
                <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-gold-100 text-gold-700 rounded-full text-xs font-medium">
                  <Star className="w-3 h-3 fill-current" />
                  Default
                </div>
              )}
              
              {/* Inactive Badge */}
              {!account.is_active && (
                <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
                  <XCircle className="w-3 h-3" />
                  Inactive
                </div>
              )}
              
              {/* Account Info */}
              <div className="mb-4">
                <h3 className="font-semibold text-lg text-gray-900">{account.account_name}</h3>
                <p className="text-gray-500 text-sm">{account.bank_name}</p>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Routing #</span>
                  <span className="font-mono">{account.routing_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Account #</span>
                  <span className="font-mono">****{account.account_number_last4}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Next Check #</span>
                  <span className="font-semibold text-gold-600">{account.next_check_number}</span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(account)}
                    title="Edit"
                    data-testid={`edit-account-${account.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(account)}
                    className="text-red-600 hover:text-red-700"
                    title="Delete"
                    data-testid={`delete-account-${account.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                {!account.is_default && account.is_active && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetDefault(account)}
                    data-testid={`set-default-${account.id}`}
                  >
                    <Star className="w-4 h-4 mr-1" />
                    Set Default
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        if (!open) {
          setShowModal(false);
          setEditingAccount(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="account_name">Account Name *</Label>
              <Input
                id="account_name"
                value={formData.account_name}
                onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                placeholder="e.g., Operating Account"
                data-testid="account-name-input"
              />
              <p className="text-xs text-gray-500 mt-1">A friendly name to identify this account</p>
            </div>
            
            <div>
              <Label htmlFor="bank_name">Bank Name *</Label>
              <Input
                id="bank_name"
                value={formData.bank_name}
                onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                placeholder="e.g., Chase Bank"
                data-testid="bank-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="routing_number">Routing Number *</Label>
              <Input
                id="routing_number"
                value={formData.routing_number}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 9);
                  setFormData({ ...formData, routing_number: val });
                }}
                placeholder="123456789"
                maxLength={9}
                className="font-mono"
                data-testid="routing-number-input"
              />
              <p className="text-xs text-gray-500 mt-1">9-digit ABA routing number</p>
            </div>
            
            <div>
              <Label htmlFor="account_number">Account Number *</Label>
              <div className="relative">
                <Input
                  id="account_number"
                  type={showAccountNumber ? 'text' : 'password'}
                  value={formData.account_number}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 17);
                    setFormData({ ...formData, account_number: val });
                  }}
                  placeholder={editingAccount ? '****' + (editingAccount.account_number_last4 || '') + ' (leave blank to keep)' : 'Enter account number'}
                  className="font-mono pr-10"
                  data-testid="account-number-input"
                />
                <button
                  type="button"
                  onClick={() => setShowAccountNumber(!showAccountNumber)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showAccountNumber ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {editingAccount && (
                <p className="text-xs text-gray-500 mt-1">Leave blank to keep existing account number</p>
              )}
            </div>
            
            {!editingAccount && (
              <div>
                <Label htmlFor="starting_check_number">Starting Check Number *</Label>
                <Input
                  id="starting_check_number"
                  type="number"
                  value={formData.starting_check_number}
                  onChange={(e) => setFormData({ ...formData, starting_check_number: parseInt(e.target.value) || 1 })}
                  min={1}
                  data-testid="starting-check-number-input"
                />
                <p className="text-xs text-gray-500 mt-1">First check number for this account</p>
              </div>
            )}
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="is_default" className="cursor-pointer">Set as Default</Label>
                <p className="text-xs text-gray-500">Use this account for new checks</p>
              </div>
              <Switch
                id="is_default"
                checked={formData.is_default}
                onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                data-testid="is-default-switch"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowModal(false);
                setEditingAccount(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-account-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingAccount ? 'Update' : 'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBankAccounts;
